/**
 * Metadane toolow asystenta WiseAi — uzywane przez ToolCallCard i dispatcher widokow.
 *
 * Konwencja nazw z backendu (Wise/AssistantAi/Domain/Orchestration/RemoteApi):
 *  - `WiseB2B_<METHOD>_api_ui_<sciezka>` — np. `WiseB2B_GET_api_ui_products`.
 *
 * Tutaj mapujemy nazwy techniczne na opis dla usera ("Wyszukiwanie produktow")
 * oraz typ widoku ktory ma sie pojawic po zakonczeniu (`viewType`).
 */

export type ToolViewType =
  | 'products'
  | 'cart'
  | 'carts'
  | 'clients'
  | 'offers'
  | 'tasks'
  | 'deals'
  | 'dashboard'
  | 'generic';

export interface ToolMeta {
  label: string; // "Wyszukiwanie produktow"
  pendingLabel: string; // "Szukam produktow..."
  viewType: ToolViewType;
}

/**
 * Backend buduje nazwy toolow z OpenAPI: `WiseB2B_<METHOD>_<sciezka>` gdzie kazdy
 * `/` zamieniony jest na `_`, a parametry `{cartId}` zamieniane sa na `byC` (collection by id).
 * Stad np. `POST /api/ui/carts/{cartId}/positions` → `WiseB2B_POST_api_ui_carts_byC_positions`.
 *
 * Pattern dopasowujemy do KONCOWKI nazwy (suffix po `api_ui_`).
 *
 * Labele to **klucze i18n** — komponent ToolCallCard tlumaczy je przez `t()`. Tak samo
 * `pendingLabel`. Brak klucza w pliku translacji = wyswietlanie surowego tekstu (acceptable).
 */
const PATTERNS: Array<{ test: RegExp; getMeta: (method: string) => ToolMeta }> = [
  // POST /carts/{cartId}/add-product-to-cart -> WiseB2B_POST_api_ui_carts_byC_add_product_to_cart
  {
    test: /carts_byC_add_product_to_cart$/i,
    getMeta: () => ({
      label: 'Dodawanie produktu do koszyka',
      pendingLabel: 'Dodaję do koszyka...',
      viewType: 'cart'
    })
  },
  // POST/DELETE /carts/{cartId}/positions -> _carts_byC_positions (lub _cartId_positions)
  {
    test: /carts_(byC|cartId)_positions$/i,
    getMeta: (m) => ({
      label: m === 'POST' ? 'Dodawanie pozycji do koszyka' : m === 'DELETE' ? 'Usuwanie pozycji koszyka' : 'Pozycje koszyka',
      pendingLabel: m === 'POST' ? 'Dodaję do koszyka...' : 'Pobieram pozycje koszyka...',
      viewType: 'cart'
    })
  },
  // POST /carts/{cartId}/positions/{positionId}/quantity-(de|in)crement
  {
    test: /carts_(byC|cartId)_positions_(byC|positionId)_quantity[-_](increment|decrement)$/i,
    getMeta: () => ({
      label: 'Zmiana ilości w koszyku',
      pendingLabel: 'Aktualizuję koszyk...',
      viewType: 'cart'
    })
  },
  // GET /carts/{cartId}/payment-methods, /delivery-methods, /maindata, /confirm, etc.
  {
    test: /carts_(byC|cartId)_[a-z_-]+$/i,
    getMeta: (m) => ({
      label: m === 'POST' ? 'Aktualizacja koszyka' : 'Szczegóły koszyka',
      pendingLabel: m === 'POST' ? 'Aktualizuję koszyk...' : 'Pobieram koszyk...',
      viewType: 'cart'
    })
  },
  // GET/POST /carts -> ostatni segment 'carts'
  {
    test: /carts$/i,
    getMeta: (m) => ({
      label: m === 'POST' ? 'Tworzenie koszyka' : 'Lista koszyków',
      pendingLabel: m === 'POST' ? 'Tworzę koszyk...' : 'Pobieram koszyki...',
      viewType: 'carts'
    })
  },
  // GET /products, /products/filters-main, /products/categories
  {
    test: /products(_filters[-_a-z]*|_categories)?$/i,
    getMeta: () => ({
      label: 'Wyszukiwanie produktów',
      pendingLabel: 'Szukam produktów...',
      viewType: 'products'
    })
  },
  // === Panel Handlowca ===
  {
    test: /sales_crm_clients(_dictionary)?$|panel_clients_clients$/i,
    getMeta: (m) => ({
      label: m === 'POST' ? 'Tworzenie klienta' : m === 'PUT' || m === 'PATCH' ? 'Aktualizacja klienta' : m === 'DELETE' ? 'Usuwanie klienta' : 'Lista klientów',
      pendingLabel: m === 'POST' ? 'Tworzę klienta...' : m === 'PUT' || m === 'PATCH' ? 'Aktualizuję klienta...' : m === 'DELETE' ? 'Usuwam klienta...' : 'Pobieram klientów...',
      // POST/PUT/PATCH/DELETE zwracaja pojedynczy obiekt - nie probujemy renderowac jako lista.
      viewType: (m === 'GET' || m === '') ? 'clients' : 'generic'
    })
  },
  {
    test: /sales_crm_my_offers$|sales_crm_offers$|sales_crm_trader_offers_semantic_search$/i,
    getMeta: (m) => ({
      label: m === 'POST' ? 'Tworzenie oferty' : m === 'PUT' || m === 'PATCH' ? 'Aktualizacja oferty' : m === 'DELETE' ? 'Usuwanie oferty' : 'Lista ofert',
      pendingLabel: m === 'POST' ? 'Tworzę ofertę...' : m === 'PUT' || m === 'PATCH' ? 'Aktualizuję ofertę...' : m === 'DELETE' ? 'Usuwam ofertę...' : 'Pobieram oferty...',
      viewType: (m === 'GET' || m === '') ? 'offers' : 'generic'
    })
  },
  {
    test: /sales_crm_tasks$/i,
    getMeta: (m) => ({
      label: m === 'POST' ? 'Tworzenie zadania' : m === 'PUT' || m === 'PATCH' ? 'Aktualizacja zadania' : m === 'DELETE' ? 'Usuwanie zadania' : 'Lista zadań',
      pendingLabel: m === 'POST' ? 'Tworzę zadanie...' : m === 'PUT' || m === 'PATCH' ? 'Aktualizuję zadanie...' : m === 'DELETE' ? 'Usuwam zadanie...' : 'Pobieram zadania...',
      viewType: (m === 'GET' || m === '') ? 'tasks' : 'generic'
    })
  },
  {
    test: /sales_crm_dashboard$|trader_insights_dashboard$/i,
    getMeta: () => ({
      label: 'Dashboard handlowca',
      pendingLabel: 'Pobieram dane dashboardu...',
      viewType: 'dashboard'
    })
  },
  // === PHP-native DealToolSet (nazwy = nazwy metod PHP) ===
  {
    test: /^show_?deals_?kanban$/i,
    getMeta: () => ({
      label: 'Kanban transakcji',
      pendingLabel: 'Pobieram kanban transakcji...',
      viewType: 'deals'
    })
  },
  {
    test: /^get_?deal_?details$/i,
    getMeta: () => ({
      label: 'Szczegóły transakcji',
      pendingLabel: 'Pobieram szczegóły transakcji...',
      viewType: 'deals'
    })
  },
  {
    test: /^list_?deals$/i,
    getMeta: () => ({
      label: 'Lista transakcji',
      pendingLabel: 'Pobieram transakcje...',
      viewType: 'deals'
    })
  },
  {
    test: /^add_?deal_?note$/i,
    getMeta: () => ({
      label: 'Dodawanie notatki do transakcji',
      pendingLabel: 'Dodaję notatkę...',
      viewType: 'generic'
    })
  },
  {
    test: /^add_?deal_?call$/i,
    getMeta: () => ({
      label: 'Odnotowanie połączenia',
      pendingLabel: 'Zapisuję połączenie...',
      viewType: 'generic'
    })
  },
  {
    test: /^add_?deal_?email$/i,
    getMeta: () => ({
      label: 'Odnotowanie wiadomości e-mail',
      pendingLabel: 'Zapisuję wiadomość...',
      viewType: 'generic'
    })
  },
  {
    test: /^add_?deal_?meeting$/i,
    getMeta: () => ({
      label: 'Odnotowanie spotkania',
      pendingLabel: 'Zapisuję spotkanie...',
      viewType: 'generic'
    })
  },
  {
    test: /^change_?deal_?stage$/i,
    getMeta: () => ({
      label: 'Zmiana etapu transakcji',
      pendingLabel: 'Zmieniam etap transakcji...',
      viewType: 'generic'
    })
  },
  {
    test: /^reopen_?deal$/i,
    getMeta: () => ({
      label: 'Otwieranie transakcji ponownie',
      pendingLabel: 'Otwieram transakcję ponownie...',
      viewType: 'generic'
    })
  },
  {
    test: /^assign_?offer_?to_?deal$/i,
    getMeta: () => ({
      label: 'Przypisywanie oferty do transakcji',
      pendingLabel: 'Przypisuję ofertę...',
      viewType: 'generic'
    })
  },
  {
    test: /^list_?deal_?offers$/i,
    getMeta: () => ({
      label: 'Oferty transakcji',
      pendingLabel: 'Pobieram oferty transakcji...',
      viewType: 'offers'
    })
  },
  {
    test: /^search_?client_?by_?name$/i,
    getMeta: () => ({
      label: 'Wyszukiwanie klienta',
      pendingLabel: 'Szukam klienta...',
      viewType: 'clients'
    })
  },
  {
    test: /^add_?activity_?to_?client_?active_?deals$/i,
    getMeta: () => ({
      label: 'Dodawanie aktywności do transakcji klienta',
      pendingLabel: 'Dodaję aktywność do aktywnych transakcji...',
      viewType: 'generic'
    })
  },
  {
    test: /^get_?client_?briefing$/i,
    getMeta: () => ({
      label: 'Briefing klienta',
      pendingLabel: 'Przygotowuję briefing klienta...',
      viewType: 'clients'
    })
  }
];

const HUMAN_FALLBACK_LABEL = 'Akcja';

/**
 * Slownik metod HTTP na czasowniki po polsku — uzywany przy fallbacku gdy zaden pattern
 * nie matchuje toola. Lepsze niz suche "POST /carts/...". Labele sa kluczami i18n.
 */
const METHOD_VERBS: Record<string, string> = {
  GET: 'Pobieranie',
  POST: 'Tworzenie',
  PUT: 'Aktualizacja',
  PATCH: 'Aktualizacja',
  DELETE: 'Usuwanie'
};

const humanizeFunctionName = (functionName: string): { method: string; label: string } => {
  const match = functionName.match(/^WiseB2B_([A-Z]+)_api_ui_(.*)$/);
  if (!match) {
    return { method: 'GET', label: functionName };
  }
  const method = match[1];
  // Zbuduj sciezke z normalizacja placeholderow + dashes:
  //  - `byC` (backend convention zamienia `{cartId}` na `byC`) -> `…`
  //  - `cartId`/`productId`/`id` -> `…`
  //  - `_` -> `/`
  //  - segmenty z czesciami slownikowymi z myslnikami zachowujemy (np. `add_product_to_cart`)
  const segments = match[2].split('_');
  const cleanedSegments: string[] = [];
  for (const seg of segments) {
    if (seg === 'byC' || seg === 'cartId' || seg === 'productId' || seg === 'id' || seg === 'positionId') {
      // pomijamy placeholdery — i tak nie wnosza informacji uzytkownikowi
      continue;
    }
    cleanedSegments.push(seg.replace(/-/g, ' '));
  }
  // Resource = ostatni "logiczny" segment, np. "products", "carts", "add product to cart"
  const resource = cleanedSegments.length > 0 ? cleanedSegments[cleanedSegments.length - 1] : '';
  const verb = METHOD_VERBS[method] ?? method;
  const label = resource ? `${verb}: ${resource.replace(/\b\w/g, (c) => c.toUpperCase())}` : verb;
  return { method, label };
};

export const resolveToolMeta = (functionName: string | undefined | null): ToolMeta => {
  if (!functionName) {
    return { label: HUMAN_FALLBACK_LABEL, pendingLabel: 'Wykonuję akcję...', viewType: 'generic' };
  }
  const methodMatch = functionName.match(/^WiseB2B_([A-Z]+)_/);
  const method = methodMatch ? methodMatch[1] : 'GET';
  for (const { test, getMeta } of PATTERNS) {
    if (test.test(functionName)) return getMeta(method);
  }
  // Fallback — ludzka, ale techniczna nazwa.
  const { label } = humanizeFunctionName(functionName);
  return {
    label,
    pendingLabel: `Wykonuję: ${label}...`,
    viewType: 'generic'
  };
};

/**
 * Bezpiecznie parsuje `content` wiadomosci role=`tool` (JSON string z odpowiedzia API).
 *
 * Backend (`RemoteApiToolDispatcher`) tnie odpowiedzi powyzej 8 KB doklejajac
 * ` ... [truncated]` — co lamie JSON. Probujemy odzyskac dane:
 *  1. najpierw probujemy parsowac as-is,
 *  2. jezeli content zawiera marker `[truncated]`, wycinamy ostatni niedomkniety
 *     item z tablicy `items[]` i probujemy ponownie.
 */
const recoverTruncatedJson = (raw: string): string | null => {
  // Wytnij marker i wszystko po nim.
  const stripped = raw.replace(/\s*[.…]+\s*\[truncated\]\s*$/i, '');
  // Szukamy ostatniego prawidlowego zamkniecia "},{" w tablicy items —
  // tam mozemy bezpiecznie przerwac liste i domknac JSON.
  const lastSafeItemEnd = stripped.lastIndexOf('},{');
  if (lastSafeItemEnd === -1) return null;
  // Zachowaj do "}" wlacznie — utniemy resztke i domkniemy `],"...":"...":...}`
  const truncated = stripped.slice(0, lastSafeItemEnd + 1);
  // Spadnij sekwencja klamer: zamknij items array + outer objects.
  // Liczymy nadmiar otwarc `{` `[` zeby je domknac.
  let openCurly = 0;
  let openSquare = 0;
  let inString = false;
  let escape = false;
  for (let i = 0; i < truncated.length; i += 1) {
    const ch = truncated[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (ch === '\\') {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === '{') openCurly += 1;
    else if (ch === '}') openCurly -= 1;
    else if (ch === '[') openSquare += 1;
    else if (ch === ']') openSquare -= 1;
  }
  // Domknij wszystkie nadmiar otwartych nawiasow.
  let closing = '';
  while (openSquare > 0) { closing += ']'; openSquare -= 1; }
  while (openCurly > 0) { closing += '}'; openCurly -= 1; }
  return truncated + closing;
};

/**
 * Rekonstruuje HTTP method + path z technicznej nazwy toola.
 * Konwencja backend: `WiseB2B_<METHOD>_api_ui_<sciezka_z__zamiast_/>` gdzie
 * placeholdery typu `{cartId}` zostaja jako goly identifier np. `cartId`.
 *
 * Przyklad: `WiseB2B_POST_api_ui_carts_cartId_positions`
 *        →  { method: 'POST', path: '/api/ui/carts/{cartId}/positions' }
 *
 * Identyfikatory rozpoznajemy heurystycznie po wzorcu `*Id` (cartId, productId, id).
 */
export const decomposeToolFunctionName = (
  functionName: string | undefined | null
): { method: string; path: string } | null => {
  if (!functionName) return null;
  const match = functionName.match(/^[A-Za-z0-9]+_([A-Z]+)_(.+)$/);
  if (!match) return null;
  const method = match[1];
  // Segmenty rozdzielamy po `_`; rekonstrukcja `/` z `_`. Placeholdery (np. `cartId`,
  // `productId`, `id` lub backendowe `byC` - skrot OpenAPI dla path parameterow)
  // opakowujemy w `{}` dla czytelnosci. Skladamy tez sekwencje slow oddzielone `_`
  // ktore tworza jedna sciezke (np. `add_product_to_cart` -> `add-product-to-cart`)
  // przez heurystyke: ciag malych liter bezposrednio po sobie tworzy segment-mysliki.
  const segments = match[2].split('_');
  const reconstructed: string[] = [];
  let buffer: string[] = [];
  const flushBuffer = () => {
    if (buffer.length > 0) {
      reconstructed.push(buffer.join('-'));
      buffer = [];
    }
  };
  for (const seg of segments) {
    if (seg === 'api' || seg === 'ui') {
      flushBuffer();
      reconstructed.push(seg);
      continue;
    }
    // Backend zamienia parametry sciezki na `byId`/`by<X>` (np. `{cartId}` -> `byC`,
    // `{offerId}` -> `byO`, `{clientId}` -> `byC`). Wszystkie te wzorce traktujemy
    // jako placeholder — wyswietlamy jako `…` bo bez kontekstu nie wiemy ktore ID.
    if (seg === 'byId' || /^by[A-Z]$/.test(seg)) {
      flushBuffer();
      reconstructed.push('…');
      continue;
    }
    if (seg === 'id' || /^[a-z][a-zA-Z]*Id$/.test(seg)) {
      flushBuffer();
      reconstructed.push(`{${seg}}`);
      continue;
    }
    // Heurystyka: jeden segment ze slowem (cala mala literka, krotsze niz "carts"+) doklejamy
    // do buffera zeby sklejac np. "add" + "product" + "to" + "cart" -> "add-product-to-cart".
    // Wieksze segmenty (carts, products, payments) flushujemy jako osobne.
    const isJoiningWord = seg.length <= 3 || ['product', 'cart'].includes(seg);
    if (isJoiningWord && buffer.length > 0) {
      buffer.push(seg);
    } else if (buffer.length > 0) {
      // Nowy segment, ale w buforze cos jest - zakoncz bufor i zacznij nowy.
      flushBuffer();
      reconstructed.push(seg);
    } else {
      // Zaczynamy bufor — jezeli to "joining word" wpadnie do buffera, inaczej standalone.
      if (isJoiningWord) {
        buffer.push(seg);
      } else {
        reconstructed.push(seg);
      }
    }
  }
  flushBuffer();
  return { method, path: `/${reconstructed.join('/')}` };
};

export const safeParseToolResult = <T = unknown>(content: string | undefined | null): T | null => {
  if (!content) return null;
  try {
    return JSON.parse(content) as T;
  } catch {
    // Probujemy recovery dla obcietych odpowiedzi.
    if (content.includes('[truncated]')) {
      const recovered = recoverTruncatedJson(content);
      if (recovered) {
        try {
          return JSON.parse(recovered) as T;
        } catch {
          // Nie udalo sie nawet po recovery — odpuszczamy.
        }
      }
    }
    return null;
  }
};
